const clampColorBandProgress = (value) => Math.min(1, Math.max(0, value));

const easeColorBandProgress = (value) => value * value * (3 - 2 * value);

class TootoColorBandReveal extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.scene = this.querySelector('[data-tooto-color-band-scene]');
    this.stage = this.querySelector('[data-tooto-color-band-stage]');
    this.media = this.querySelector('[data-tooto-color-band-media]');
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.animationDuration = Math.max(300, Number(this.dataset.animationDuration) || 1200);

    if (!this.scene || !this.stage || !this.media) return;

    if (this.dataset.scrollEnabled !== 'true' || this.reducedMotion) {
      this.showCompleteState();
      return;
    }

    this.frameRequest = 0;
    this.render = this.render.bind(this);
    this.classList.add('is-ready');
    this.applyProgress(0);

    if (!('IntersectionObserver' in window)) {
      this.playReveal();
      return;
    }

    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.intersectionObserver?.disconnect();
        this.playReveal();
      },
      { threshold: 0.32, rootMargin: '0px 0px -5% 0px' }
    );
    this.intersectionObserver.observe(this);
  }

  disconnectedCallback() {
    this.intersectionObserver?.disconnect();
    if (this.frameRequest) cancelAnimationFrame(this.frameRequest);
  }

  showCompleteState() {
    this.classList.add('is-static');
    this.applyProgress(1);
  }

  playReveal() {
    if (this.hasPlayed) return;
    this.hasPlayed = true;

    const image = this.media.querySelector('img');
    if (image && !image.complete) {
      const startWhenReady = () => this.startRevealAnimation();
      image.addEventListener('load', startWhenReady, { once: true });
      image.addEventListener('error', startWhenReady, { once: true });
      return;
    }

    this.startRevealAnimation();
  }

  startRevealAnimation() {
    if (this.animationStarted || !this.isConnected) return;
    this.animationStarted = true;
    this.animationStartTime = performance.now();
    this.frameRequest = requestAnimationFrame(this.render);
  }

  render(timestamp) {
    this.frameRequest = 0;
    if (!this.isConnected) return;
    const progress = clampColorBandProgress((timestamp - this.animationStartTime) / this.animationDuration);
    this.applyProgress(progress);

    if (progress < 1) this.frameRequest = requestAnimationFrame(this.render);
  }

  applyProgress(progress) {
    const easedProgress = easeColorBandProgress(clampColorBandProgress(progress));
    const revealEdge = -10 + 120 * easedProgress;

    this.style.setProperty('--tooto-color-band-progress', easedProgress.toFixed(4));
    this.style.setProperty('--tooto-color-band-reveal-edge', `${revealEdge.toFixed(3)}%`);
  }
}

if (!customElements.get('tooto-color-band-reveal')) {
  customElements.define('tooto-color-band-reveal', TootoColorBandReveal);
}
